import { useEffect, useMemo, useState } from 'react';

import { MenuItem } from '@mui/material';
import { Address4, Address6 } from 'ip-address';
import { BigInteger } from 'jsbn';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { PAGE_IP_NETWORKS } from 'containers/IPNetworks';
import ButtonSubmitData from 'shared/components/Button/ButtonSubmitData/ButtonSubmitData';
import InputForm from 'shared/components/Input/InputForm';
import Loader from 'shared/containers/Loader';
import { GENERAL_INFO_TITLE, SETTINGS_TITLE } from 'shared/containers/ViewDetails/ViewDetails';
import { APIErrorT } from 'shared/models/api';
import { IPNetworkT, MulticastInfoT } from 'shared/models/ip-networks';
import api from 'utils/api';

import IPNetworksItemFormMulticastInfos from './IPNetworksItemFormMulticastInfos/IPNetworksItemFormMulticastInfos';
import validator from './IPNetworksManageData.validator';

type FormValues = {
  name: string;
  addressType: string;
  prefix: string;
  first: string;
  last: string;
  multicastInfos: Array<MulticastInfoT>;
};
function IPNetworksManageData() {
  const [defaultFormValues, setDefaultFormValues] = useState<FormValues>({
    name: '',
    addressType: 'V4',
    prefix: '',
    first: '',
    last: '',
    multicastInfos: [],
  });
  const history = useHistory();
  const params: any = useParams();
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    reset,
    control,
    formState: {
      isDirty,
      isSubmitting,
      errors,
    },
  } = useForm<FormValues>({
    mode: 'onChange',
    resolver: validator,
    defaultValues: useMemo(() => defaultFormValues, [defaultFormValues]),
  });
  const [isLoading, setIsLoading] = useState(false);
  // Trick: We have to do this in order to get the current addressType
  // OnChange of addressType value by default the form do no rerender
  // The value in getValues().addressType stay the same but is needed
  // in the multicast maybe there are a better way to manage that
  const [addressType, setAddressType] = useState('');
  const [mutlicastInfos, setMulticastInfos] = useState<MulticastInfoT[]>([]);

  const isEdit = () => params.id !== undefined;

  const initExistingValues = (data: IPNetworkT) => {
    const newValues = { ...data };
    setDefaultFormValues(newValues);
    reset(newValues);
    setAddressType(data.addressType);
    setMulticastInfos(data.multicastInfos);
  };
  const loadExistingData = () => {
    setIsLoading(true);
    api.IPNetwork.getOne(params.id)
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              const errorMessage = error?.message;
              if (errorMessage === 'failed to get pool') {
                toast.error('IP Network doesn\'t exists');
              } else {
                toast.error(errorMessage);
              }
              setIsLoading(false);
              history.push(PAGE_IP_NETWORKS);
            });
        } else {
          resp.json()
            .then((data: IPNetworkT) => {
              initExistingValues(data);
              setIsLoading(false);
            });
        }
      });
  };

  useEffect(() => {
    if (isEdit()) {
      loadExistingData();
    } else {
      setAddressType('V4');
    }
  }, []);

  const onSubmit: SubmitHandler<FormValues> = (formData: FormValues) => {
    const newData = {
      ...formData,
    };
    const request = isEdit()
      ? api.IPNetwork.update(params.id as string, newData)
      : api.IPNetwork.register(newData);
    request
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
            });
        } else {
          resp.json()
            .then(() => {
              toast.success(`IP Network has been ${isEdit() ? 'updated' : 'created'}`);
              history.push(isEdit() ? `/ip-networks/${params.id}` : '/ip-networks');
            });
        }
      });
  };

  const generateFirstLastValues = (ip: string) => {
    try {
      let prefixAddress;
      let fromBigIntegerFn;
      const adjust = new BigInteger('1');

      if (addressType === 'V4') {
        prefixAddress = new Address4(ip);
        fromBigIntegerFn = Address4.fromBigInteger;
      } else {
        prefixAddress = new Address6(ip);
        fromBigIntegerFn = Address6.fromBigInteger;
      }
      const bn = prefixAddress.startAddressExclusive().bigInteger();
      const nextBn = bn.add(adjust);
      const startAddress = fromBigIntegerFn(nextBn);

      setValue('first', startAddress.correctForm());
      setValue('last', prefixAddress.endAddressExclusive().correctForm());
    } catch (e) {
      toast.error('Automatic generating first and last values failed');
    }
  };

  const valuePrefixEdited = () => {
    const value = getValues().prefix;
    if (value !== '' && !errors.prefix) {
      generateFirstLastValues(value);
    }
  };

  if (isLoading) {
    return <Loader.Page />;
  }
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="title__header">
        <h1>{ `IP Network · ${isEdit() ? 'Edit' : 'Create'} `}</h1>
        <div className="title__header--actions">
          <ButtonSubmitData
            isEdit={isEdit()}
            disabled={(!isDirty || isSubmitting) && !isEdit()}
            isSubmitting={isSubmitting}
          />
        </div>
      </div>
      <div className="form scroll">
        <div className="form__section">
          <h3>{ GENERAL_INFO_TITLE }</h3>
          <InputForm.TextField
            fullWidth
            sx={{ m: 1 }}
            label="Name"
            register={register('name')}
            errorField={errors.name}
            disabled={isEdit()}
          />
        </div>
        <div className="form__section">
          <h3>{ SETTINGS_TITLE }</h3>
          <InputForm.Select
            fullWidth
            sx={{ m: 1 }}
            label="Select the address type"
            name="addressType"
            control={control}
            defaultValue={addressType}
            onChange={(event) => {
              const addressTypeValue = event.target.value as string;
              setAddressType(addressTypeValue);
              setValue('prefix', '');
              setValue('first', '');
              setValue('last', '');
              setValue('multicastInfos', []); // Need to reset inside the component as well
              setMulticastInfos([]);
            }}
            disabled={isEdit()}
          >
            <MenuItem value="V4">IPv4</MenuItem>
            <MenuItem value="V6">IPv6</MenuItem>
          </InputForm.Select>
          <InputForm.TextField
            fullWidth
            sx={{ m: 1 }}
            label="Prefix"
            register={register('prefix')}
            errorField={errors.prefix}
            onBlur={() => valuePrefixEdited()}
            disabled={isEdit()}
            shrinkLabel
          />
          <div className="form__side-by-side">
            <InputForm.TextField
              fullWidth
              sx={{ m: 1 }}
              label="First"
              register={register('first')}
              errorField={errors.first}
              disabled={isEdit()}
              shrinkLabel
            />
            <InputForm.TextField
              fullWidth
              sx={{ m: 1 }}
              label="Last"
              register={register('last')}
              errorField={errors.last}
              disabled={isEdit()}
              shrinkLabel
            />
          </div>
          <IPNetworksItemFormMulticastInfos
            addressType={addressType}
            onChange={(newData) => {
              setMulticastInfos(newData);
              setValue('multicastInfos', newData);
            }}
            defaultValues={mutlicastInfos}
          />
        </div>
      </div>
    </form>
  );
}

export default IPNetworksManageData;
